import React, { useState } from 'react';
import { setToken } from '../utils/auth';
import './Login.css';

const Star612Login = ({ onLoginSuccess, onClose, initialTab = 'login' }) => {
  const [isLogin, setIsLogin] = useState(initialTab === 'login'); // true: 로그인, false: 회원가입
  const [formData, setFormData] = useState({
    email: '',
    nickname: '',
    password: '',
    name: '', // 이름(일반용, 선택) - 거래 서비스는 별도 실명 필수
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        // 로그인 - star612.net 일반 사용자용 (이메일/패스워드만)
        const apiUrl = window.location.origin.includes('localhost')
          ? 'http://localhost:5000/api/auth/star612/login'
          : '/api/auth/star612/login';

        console.log('🔐 star612.net 로그인 시도:', { apiUrl, email: formData.email });

        let response;
        let abortController;
        try {
          abortController = new AbortController();
          const timeoutId = setTimeout(() => abortController.abort(), 30000);

          response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: formData.email,
              password: formData.password,
            }),
            signal: abortController.signal
          });
          
          clearTimeout(timeoutId);
        } catch (fetchError) {
          if (fetchError.name === 'AbortError' || fetchError.name === 'TimeoutError') {
            setError('요청 시간이 초과되었습니다. 서버 상태를 확인해주세요.');
          } else if (fetchError.name === 'TypeError' && fetchError.message.includes('Failed to fetch')) {
            setError('서버에 연결할 수 없습니다. 네트워크 연결과 서버 상태를 확인해주세요.');
          } else {
            setError(`연결 오류: ${fetchError.message}`);
          }
          console.error('❌ 네트워크 에러:', fetchError);
          return;
        }

        if (response.status === 502 || response.status === 503) {
          setError('서버 연결 오류입니다. 잠시 후 다시 시도해주세요.');
          return;
        }

        const contentType = response.headers.get('content-type');
        let data;
        if (contentType && contentType.includes('application/json')) {
          try {
            data = await response.json();
          } catch (jsonError) {
            console.error('❌ JSON 파싱 실패:', jsonError);
            setError('서버 응답 형식 오류가 발생했습니다.');
            return;
          }
        } else {
          const textResponse = await response.text();
          console.error('❌ 비JSON 응답:', {
            status: response.status,
            contentType,
            preview: textResponse.substring(0, 200)
          });
          setError('서버 오류가 발생했습니다. 관리자에게 문의해주세요.');
          return;
        }

        if (response.ok) {
          // star612.net 일반 사용자는 별도 토큰 저장 (trading-service와 구분)
          if (data.token) {
            // localStorage에 별도 키로 저장하여 사용자 타입 구분
            localStorage.setItem('star612_token', data.token);
            localStorage.setItem('user_type', 'star612'); // 사용자 타입 구분
          }
          
          console.log('✅ star612.net 로그인 성공:', {
            userId: data.user?.id,
            email: data.user?.email,
            nickname: data.user?.nickname,
            userType: 'star612'
          });
          
          if (onLoginSuccess) {
            onLoginSuccess({ ...data.user, userType: 'star612' }, data.token);
          }
          if (onClose) {
            onClose();
          }
        } else {
          const errorMessage = data.error || data.message || '로그인 실패';
          const is401 = response.status === 401;
          const friendlyMessage = is401
            ? '이메일 또는 비밀번호를 확인해 주세요. 거래 서비스(/trading) 계정은 별도로 가입해야 합니다.'
            : errorMessage;
          setError(friendlyMessage);
          console.error('❌ 로그인 실패:', {
            status: response.status,
            error: errorMessage,
            data
          });
          // 401 에러인 경우 회원가입 안내
          if (is401 && (errorMessage.includes('일치하지 않습니다') || errorMessage.toLowerCase().includes('invalid'))) {
            setTimeout(() => {
              if (window.confirm('등록된 계정이 없습니다. 회원가입을 진행하시겠습니까?')) {
                setIsLogin(false);
                setError('');
              }
            }, 1500);
          }
        }
      } else {
        // 회원가입 - star612.net 일반 사용자용
        const apiUrl = window.location.origin.includes('localhost') 
          ? 'http://localhost:5000/api/auth/star612/register'
          : '/api/auth/star612/register';
        
        console.log('📝 star612.net 회원가입 시도:', { apiUrl, email: formData.email, nickname: formData.nickname });
        
        let response;
        let abortController;
        try {
          abortController = new AbortController();
          const timeoutId = setTimeout(() => abortController.abort(), 30000);
          
          response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: formData.email,
              nickname: formData.nickname,
              password: formData.password,
              name: formData.name || undefined,
            }),
            signal: abortController.signal
          });
          
          clearTimeout(timeoutId);
        } catch (fetchError) {
          if (fetchError.name === 'AbortError' || fetchError.name === 'TimeoutError') {
            setError('요청 시간이 초과되었습니다. 서버 상태를 확인해주세요.');
          } else if (fetchError.name === 'TypeError' && fetchError.message.includes('Failed to fetch')) {
            setError('서버에 연결할 수 없습니다. 네트워크 연결과 서버 상태를 확인해주세요.');
          } else {
            setError(`연결 오류: ${fetchError.message}`);
          }
          console.error('❌ 네트워크 에러:', fetchError);
          return;
        }

        if (response.status === 502 || response.status === 503) {
          setError('서버 연결 오류입니다. 잠시 후 다시 시도해주세요.');
          return;
        }

        const contentType = response.headers.get('content-type');
        let data;
        if (contentType && contentType.includes('application/json')) {
          try {
            data = await response.json();
          } catch (jsonError) {
            console.error('❌ JSON 파싱 실패:', jsonError);
            setError('서버 응답 형식 오류가 발생했습니다.');
            return;
          }
        } else {
          const textResponse = await response.text();
          console.error('❌ 비JSON 응답:', {
            status: response.status,
            contentType,
            preview: textResponse.substring(0, 200)
          });
          setError('서버 오류가 발생했습니다. 관리자에게 문의해주세요.');
          return;
        }

        if (response.ok) {
          if (data.token) {
            localStorage.setItem('star612_token', data.token);
            localStorage.setItem('user_type', 'star612');
          }
          
          console.log('✅ star612.net 회원가입 성공:', {
            userId: data.user?.id,
            email: data.user?.email,
            nickname: data.user?.nickname,
            userType: 'star612'
          });
          
          if (onLoginSuccess) {
            onLoginSuccess({ ...data.user, userType: 'star612' }, data.token);
          }
          
          // 회원가입 후 현재 페이지로 리다이렉트
          window.location.reload();
          
          if (onClose) {
            onClose();
          }
        } else {
          if (response.status === 409) {
            setError('이미 등록된 이메일 또는 닉네임입니다. 로그인해주세요.');
            setTimeout(() => {
              setIsLogin(true);
            }, 2000);
          } else {
            if (data.errors && Array.isArray(data.errors)) {
              const errorMessages = data.errors.map(e => e.msg || e.message).join(', ');
              setError(errorMessages || data.error || '회원가입 실패');
            } else {
              setError(data.error || data.message || '회원가입 실패');
            }
            console.error('❌ 회원가입 실패:', {
              status: response.status,
              error: data.error,
              errors: data.errors,
              data
            });
          }
        }
      }
    } catch (err) {
      console.error('❌ 예상치 못한 에러:', err);
      if (err.name === 'AbortError' || err.name === 'TimeoutError') {
        setError('요청 시간이 초과되었습니다. 서버 상태를 확인해주세요.');
      } else if (err.message) {
        setError(`오류 발생: ${err.message}`);
      } else {
        setError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setError('');
    setLoading(false);
    if (onClose) {
      console.log('🔒 star612.net 모달 닫기 호출');
      onClose();
    }
  };

  return (
    <div className="login-overlay" onClick={handleClose}>
      <div className="login-modal" onClick={(e) => e.stopPropagation()}>
        <div className="login-header">
          <h2>{isLogin ? '로그인' : '회원가입'}</h2>
          <button 
            className="login-close" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('🔒 닫기 버튼 클릭');
              handleClose(e);
            }}
            type="button"
            aria-label="닫기"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '30px',
              color: '#999',
              cursor: 'pointer',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color 0.3s',
              zIndex: 1001,
              position: 'relative'
            }}
            onMouseEnter={(e) => e.target.style.color = '#333'}
            onMouseLeave={(e) => e.target.style.color = '#999'}
          >
            ×
          </button>
        </div>

        <div className="login-tabs">
          <button
            className={isLogin ? 'active' : ''}
            onClick={() => {
              setIsLogin(true);
              setError('');
            }}
          >
            로그인
          </button>
          <button
            className={!isLogin ? 'active' : ''}
            onClick={() => {
              setIsLogin(false);
              setError('');
            }}
          >
            회원가입
          </button>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label>이메일</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="example@email.com"
            />
          </div>

          {!isLogin && (
            <>
              <div className="form-group">
                <label>닉네임</label>
                <input
                  type="text"
                  name="nickname"
                  value={formData.nickname}
                  onChange={handleChange}
                  required
                  placeholder="사용자 닉네임"
                  maxLength="50"
                />
                <small>서비스에서 표시될 닉네임입니다</small>
              </div>
              <div className="form-group">
                <label>이름 (선택)</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="표시용 이름 (거래 서비스는 별도 실명 필요)"
                  maxLength="50"
                />
                <small>서비스에서 표시할 이름입니다. 거래 서비스(/trading)는 별도로 실명이 필요합니다.</small>
              </div>
            </>
          )}

          <div className="form-group">
            <label>비밀번호</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder={isLogin ? "비밀번호 입력" : "비밀번호 (6자 이상)"}
              minLength={isLogin ? undefined : 6}
            />
            {!isLogin && <small>비밀번호는 6자 이상이어야 합니다</small>}
          </div>

          <button
            type="submit"
            className="login-submit"
            disabled={loading}
          >
            {loading ? '처리 중...' : (isLogin ? '로그인' : '회원가입')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Star612Login;
